import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, User, Lock, Building2, Save, Loader2, Eye, EyeOff } from "lucide-react";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [company, setCompany] = useState(user?.company || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        first_name: firstName,
        last_name: lastName,
        company,
        email,
      }, { withCredentials: true });
      toast.success("Profil mis a jour");
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const changePassword = async () => {
    if (newPwd !== confirmPwd) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (newPwd.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caracteres");
      return;
    }
    setChangingPwd(true);
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: currentPwd,
        new_password: newPwd,
      }, { withCredentials: true });
      toast.success("Mot de passe modifie");
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setChangingPwd(false);
  };

  const planLabel = user?.plan === "enterprise" ? "Entreprise" : user?.plan === "pro" ? "Pro" : "Free";
  const planColor = user?.plan === "enterprise" ? "text-amber-700 bg-amber-100" : user?.plan === "pro" ? "text-blue-700 bg-blue-100" : "text-gray-600 bg-gray-100";

  return (
    <div className="min-h-screen bg-gray-50/50" data-testid="profile-page">
      <header className="h-14 bg-white/80 backdrop-blur-xl border-b border-gray-200 flex items-center px-5 sticky top-0 z-40">
        <button onClick={() => navigate("/dashboard")} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 mr-3" data-testid="profile-back-btn">
          <ArrowLeft size={16} />
        </button>
        <h1 className="font-semibold text-base text-gray-900">Mon Profil</h1>
      </header>

      <div className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Account Info */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
              <User size={20} className="text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">{email || user?.email}</p>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${planColor}`}>{planLabel}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-lg"
                data-testid="profile-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Prenom</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 rounded-lg"
                  data-testid="profile-firstname"
                />
              </div>
              <div>
                <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Nom</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 rounded-lg"
                  data-testid="profile-lastname"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Building2 size={10} /> Entreprise
              </Label>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="mt-1 rounded-lg"
                placeholder="Nom de l'entreprise"
                data-testid="profile-company"
              />
            </div>
            <button
              onClick={saveProfile}
              disabled={saving}
              className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              data-testid="profile-save-btn"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
          </div>
        </div>

        {/* Password Change */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center">
              <Lock size={20} className="text-rose-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">Changer le mot de passe</p>
              <p className="text-[10px] text-gray-400">Minimum 6 caracteres</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Mot de passe actuel</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPwd ? "text" : "password"}
                  value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)}
                  className="rounded-lg pr-10"
                  data-testid="profile-current-pwd"
                />
                <button onClick={() => setShowCurrentPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" type="button">
                  {showCurrentPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPwd ? "text" : "password"}
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    className="rounded-lg pr-10"
                    data-testid="profile-new-pwd"
                  />
                  <button onClick={() => setShowNewPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" type="button">
                    {showNewPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Confirmer</Label>
                <Input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  className="mt-1 rounded-lg"
                  data-testid="profile-confirm-pwd"
                />
              </div>
            </div>
            <button
              onClick={changePassword}
              disabled={changingPwd || !currentPwd || !newPwd}
              className="bg-rose-600 text-white px-5 py-2.5 rounded-lg text-xs font-semibold hover:bg-rose-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              data-testid="profile-change-pwd-btn"
            >
              {changingPwd ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />}
              {changingPwd ? "Modification..." : "Modifier le mot de passe"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
